'use client';

import {
  FilterBar,
  type TFilterBarPriceRange,
} from '@chemist4u/newt-components';
import { type SortOptions, sortOptions } from '@lib/util/sort-products';
import { PRODUCTS_PAGE_SIZE } from '@modules/store/lib/product-query';

type ProductListingToolbarProps = {
  sortBy: SortOptions;
  page: number;
  productCount: number;
  minPrice?: number;
  maxPrice?: number;
  /** True while a client-side sort/filter override is fetching its first
   * page — `productCount` is 0 at that point, so the results label would
   * otherwise misleadingly read "0 of 0 products". */
  isLoading?: boolean;
  onSortChange: (value: string) => void;
  onPriceApply: (range: TFilterBarPriceRange) => void;
};

// Purely controlled sibling of `ProductToolbar` — that component owns its
// own URL sync via `useSearchParams()`, which is fine for the SSR search
// templates (already fully dynamic routes) but would force `ProductListing`
// back into the Suspense/no-JS-fallback bind this component exists to avoid
// on static/ISR category & collection pages. This one takes no routing
// dependency at all; the caller owns sort/price state and the URL sync.
const ProductListingToolbar = ({
  sortBy,
  page,
  productCount,
  minPrice,
  maxPrice,
  isLoading = false,
  onSortChange,
  onPriceApply,
}: ProductListingToolbarProps) => {
  return (
    <div className="relative z-[25] mb-4">
      <FilterBar
        page={page}
        pageSize={PRODUCTS_PAGE_SIZE}
        totalItems={productCount}
        // No label override when loaded — FilterBar falls back to the DS
        // range format ("1 - 24 of 111 products"), matching the Pagination
        // results count below the grid.
        resultsLabel={isLoading ? 'Loading products…' : undefined}
        sortValue={sortBy}
        sortOptions={sortOptions}
        onSortChange={onSortChange}
        priceMin={minPrice ?? null}
        priceMax={maxPrice ?? null}
        onPriceApply={onPriceApply}
      />
    </div>
  );
};

export default ProductListingToolbar;
